import React, { Fragment, useMemo } from 'react';
import { Box } from '@mui/material';
import { CompetitionRankingRulesAlert } from '@components/competitions/CompetitionRankingRulesAlert';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { ContentPanel } from '@components/ContentPanel';
import { EmptyPlaceholder } from '@components/EmptyPlaceholder';
import { Loader } from '@components/feed/Loader';
import { RankingTable } from '@pages/Portal/Competitions/components/RankingTable';
import { useGetPortalCompetitionRanking, useGetPortalCompletedCompetitionRanking } from '@reactQuery/competitions';
import { useAuth } from '@context/AuthProvider';
import { useTranslation } from 'react-i18next';
import {
  getRankingSubmittedCountColumnLabel,
  getRankingSubmittedCountColumnTooltip,
  getRankingValueColumnLabel,
  mapPortalRankingEntryToRankingItem,
} from '../../../helpers';
import { IRankingTab } from './interfaces';

export const RankingTab: React.FC<IRankingTab> = ({
  show,
  slug,
  format,
  variant = 'current',
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const highlightedMemberSlug = currentUser?.member?.slug;
  const currentRanking = useGetPortalCompetitionRanking(
    variant === 'current' ? slug : ''
  );
  const completedRanking = useGetPortalCompletedCompetitionRanking(
    variant === 'results' ? slug : ''
  );
  const { data: ranking, isLoading } =
    variant === 'results' ? completedRanking : currentRanking;

  const rankingItems = useMemo(
    () => ranking?.data.map(mapPortalRankingEntryToRankingItem) ?? [],
    [ranking]
  );

  const rankingScoreColumnLabel = useMemo(
    () =>
      ranking?.meta.rankingMethod && ranking.meta.scoreUnit
        ? getRankingValueColumnLabel(
            ranking.meta.scoreUnit,
            ranking.meta.rankingMethod
          )
        : undefined,
    [ranking?.meta.rankingMethod, ranking?.meta.scoreUnit]
  );

  const submittedCountColumnLabel = useMemo(
    () => getRankingSubmittedCountColumnLabel(format),
    [format]
  );

  const submittedCountColumnTooltip = useMemo(
    () => getRankingSubmittedCountColumnTooltip(format),
    [format]
  );

  const rankingRules = ranking?.meta;

  if (!show) {
    return null;
  }

  if (isLoading) {
    return (
      <Box py={4}>
        <Loader />
      </Box>
    );
  }

  if (rankingItems.length === 0) {
    return (
      <ContentPanel borderRadius="none">
        <Fragment>
          {rankingRules && (
            <Box sx={{ mb: 2 }}>
              <CompetitionRankingRulesAlert
                rankingMethod={rankingRules.rankingMethod}
                minCountingScores={rankingRules.minCountingScores}
                rankingBestN={rankingRules.rankingBestN}
                format={format}
              />
            </Box>
          )}
          <EmptyPlaceholder
            icon={LeaderboardIcon}
            title={t(
              'competitions:portal.ranking.emptyPlaceholders.noRanking.title'
            )}
            subTitle={t(
              'competitions:portal.ranking.emptyPlaceholders.noRanking.subTitle'
            )}
          />
        </Fragment>
      </ContentPanel>
    );
  }

  return (
    <Fragment>
      {rankingRules && (
        <Box sx={{ mb: 2 }}>
          <CompetitionRankingRulesAlert
            rankingMethod={rankingRules.rankingMethod}
            minCountingScores={rankingRules.minCountingScores}
            rankingBestN={rankingRules.rankingBestN}
            format={format}
          />
        </Box>
      )}
      <RankingTable
        rankingData={rankingItems}
        submittedCountColumnLabel={submittedCountColumnLabel}
        submittedCountColumnTooltip={submittedCountColumnTooltip}
        scoreColumnLabel={rankingScoreColumnLabel}
        minCountingScores={ranking?.meta.minCountingScores}
        compactMetricColumns
        disableColumnSorting
        highlightedMemberSlug={highlightedMemberSlug}
      />
    </Fragment>
  );
};
